/**
 * Event Management Agent
 * Tracks farm events, farmers markets, seasonal festivals, and workshops
 */

interface FarmEvent {
  id: string;
  farmId: string;
  farmName: string;
  title: string;
  type: 'market' | 'workshop' | 'festival' | 'tour' | 'upick' | 'special';
  description: string;
  date: Date;
  startTime: string;
  endTime: string;
  location: {
    address: string;
    city: string;
    coordinates: { lat: number; lng: number };
  };
  price?: {
    min: number;
    max: number;
    currency: string;
  };
  capacity?: number;
  registered?: number;
  tags: string[];
  requiresRegistration: boolean;
  registrationUrl?: string;
  products?: string[];
  imageUrl?: string;
}

interface EventFilter {
  type?: string[];
  startDate?: Date;
  endDate?: Date;
  city?: string;
  products?: string[];
  maxDistance?: number;
  userLocation?: { lat: number; lng: number };
  priceRange?: { min: number; max: number };
}

interface EventRecommendation {
  event: FarmEvent;
  score: number;
  reasons: string[];
  distance?: number;
}

class EventManagementAgent {
  private events: FarmEvent[];
  private eventsByFarm: Map<string, FarmEvent[]>;

  constructor() {
    this.events = this.generateSampleEvents();
    this.eventsByFarm = this.indexEventsByFarm();
  }

  /**
   * Get all upcoming events
   * @param daysAhead Number of days to look ahead (default 30)
   * @returns List of upcoming events
   */
  getUpcomingEvents(daysAhead: number = 30): FarmEvent[] {
    const now = new Date();
    const endDate = new Date(now.getTime() + daysAhead * 24 * 60 * 60 * 1000);

    return this.events
      .filter(event => event.date >= now && event.date <= endDate)
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  /**
   * Filter events by criteria
   * @param filters Event filter criteria
   * @returns Filtered list of events
   */
  filterEvents(filters: EventFilter): FarmEvent[] {
    let filtered = [...this.events];

    if (filters.type && filters.type.length > 0) {
      filtered = filtered.filter(event => filters.type!.includes(event.type));
    }

    if (filters.startDate) {
      filtered = filtered.filter(event => event.date >= filters.startDate!);
    }

    if (filters.endDate) {
      filtered = filtered.filter(event => event.date <= filters.endDate!);
    }

    if (filters.city) {
      filtered = filtered.filter(event =>
        event.location.city.toLowerCase() === filters.city!.toLowerCase()
      );
    }

    if (filters.products && filters.products.length > 0) {
      filtered = filtered.filter(event =>
        event.products?.some(p => filters.products!.includes(p))
      );
    }

    if (filters.priceRange) {
      filtered = filtered.filter(event => {
        if (!event.price) return true;
        return (
          event.price.min >= filters.priceRange!.min &&
          event.price.max <= filters.priceRange!.max
        );
      });
    }

    if (filters.maxDistance && filters.userLocation) {
      filtered = filtered.filter(event => {
        const distance = this.calculateDistance(
          filters.userLocation!,
          event.location.coordinates
        );
        return distance <= filters.maxDistance!;
      });
    }

    return filtered.sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  /**
   * Get events for a specific farm
   * @param farmId Farm ID
   * @returns List of farm events
   */
  getFarmEvents(farmId: string): FarmEvent[] {
    return this.eventsByFarm.get(farmId) || [];
  }

  /**
   * Get personalized event recommendations
   * @param userPreferences User interests and location
   * @returns Recommended events with scores
   */
  getEventRecommendations(userPreferences: {
    interests: string[];
    location?: { lat: number; lng: number };
    favoriteProducts?: string[];
    favoriteFarms?: string[];
  }): EventRecommendation[] {
    const upcoming = this.getUpcomingEvents(60);
    const recommendations: EventRecommendation[] = [];

    for (const event of upcoming) {
      let score = 50; // Base score
      const reasons: string[] = [];

      // Score based on event type matching interests
      if (userPreferences.interests.includes(event.type)) {
        score += 20;
        reasons.push(`Matches your interest in ${event.type} events`);
      }

      // Score based on products
      if (
        userPreferences.favoriteProducts &&
        event.products?.some(p => userPreferences.favoriteProducts!.includes(p))
      ) {
        score += 15;
        reasons.push('Features products you love');
      }

      // Score based on favorite farms
      if (userPreferences.favoriteFarms?.includes(event.farmId)) {
        score += 25;
        reasons.push('Hosted by one of your favorite farms');
      }

      // Score based on distance
      let distance: number | undefined;
      if (userPreferences.location) {
        distance = this.calculateDistance(
          userPreferences.location,
          event.location.coordinates
        );
        if (distance < 10) {
          score += 15;
          reasons.push('Very close to your location');
        } else if (distance < 25) {
          score += 10;
          reasons.push('Conveniently located nearby');
        }
      }

      // Score based on availability
      if (event.capacity && event.registered) {
        const availability = (event.capacity - event.registered) / event.capacity;
        if (availability < 0.2) {
          score += 10;
          reasons.push('Limited spots available - book soon!');
        }
      }

      // Score based on timing
      const daysUntil = Math.floor(
        (event.date.getTime() - Date.now()) / (24 * 60 * 60 * 1000)
      );
      if (daysUntil <= 7) {
        score += 5;
        reasons.push('Coming up soon');
      }

      if (score >= 60) {
        recommendations.push({ event, score, reasons, distance });
      }
    }

    return recommendations.sort((a, b) => b.score - a.score).slice(0, 10);
  }

  /**
   * Get events by type
   * @param type Event type
   * @param limit Maximum number of results
   * @returns List of events
   */
  getEventsByType(type: FarmEvent['type'], limit: number = 10): FarmEvent[] {
    return this.events
      .filter(event => event.type === type && event.date >= new Date())
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .slice(0, limit);
  }

  /**
   * Get this weekend's events
   * @returns Weekend events
   */
  getWeekendEvents(): FarmEvent[] {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const daysUntilSaturday = dayOfWeek === 0 ? 6 : 6 - dayOfWeek;

    const saturday = new Date(now);
    saturday.setDate(now.getDate() + daysUntilSaturday);
    saturday.setHours(0, 0, 0, 0);

    const sunday = new Date(saturday);
    sunday.setDate(saturday.getDate() + 1);
    sunday.setHours(23, 59, 59, 999);

    return this.events
      .filter(event => event.date >= saturday && event.date <= sunday)
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  /**
   * Get seasonal events for current month
   * @returns Seasonal events
   */
  getSeasonalEvents(): FarmEvent[] {
    const month = new Date().getMonth();
    const seasonalTypes = ['festival', 'upick', 'special'];

    return this.events
      .filter(event => {
        return (
          event.date.getMonth() === month &&
          seasonalTypes.includes(event.type) &&
          event.date >= new Date()
        );
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  // Helper methods

  private generateSampleEvents(): FarmEvent[] {
    const now = new Date();
    const events: FarmEvent[] = [];

    // Generate sample events for the next 60 days
    for (let i = 0; i < 20; i++) {
      const date = new Date(now.getTime() + Math.random() * 60 * 24 * 60 * 60 * 1000);
      const types: FarmEvent['type'][] = ['market', 'workshop', 'festival', 'tour', 'upick', 'special'];
      const type = types[Math.floor(Math.random() * types.length)];

      events.push({
        id: `event-${i + 1}`,
        farmId: `farm-${Math.floor(Math.random() * 5) + 1}`,
        farmName: this.getRandomFarmName(),
        title: this.generateEventTitle(type),
        type,
        description: this.generateEventDescription(type),
        date,
        startTime: '9:00 AM',
        endTime: type === 'market' ? '2:00 PM' : '12:00 PM',
        location: {
          address: '123 Farm Road',
          city: ['Fort Myers', 'Naples', 'Cape Coral', 'Bonita Springs'][
            Math.floor(Math.random() * 4)
          ],
          coordinates: {
            lat: 26.6 + Math.random() * 0.2,
            lng: -81.8 + Math.random() * 0.2,
          },
        },
        price:
          Math.random() > 0.5
            ? { min: 5, max: 20, currency: 'USD' }
            : undefined,
        capacity: Math.random() > 0.6 ? 50 : undefined,
        registered: Math.random() > 0.6 ? Math.floor(Math.random() * 40) : undefined,
        tags: this.generateEventTags(type),
        requiresRegistration: Math.random() > 0.5,
        products: type === 'upick' ? this.getSeasonalProducts() : undefined,
      });
    }

    return events.sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  private indexEventsByFarm(): Map<string, FarmEvent[]> {
    const index = new Map<string, FarmEvent[]>();
    for (const event of this.events) {
      const farmEvents = index.get(event.farmId) || [];
      farmEvents.push(event);
      index.set(event.farmId, farmEvents);
    }
    return index;
  }

  private calculateDistance(
    point1: { lat: number; lng: number },
    point2: { lat: number; lng: number }
  ): number {
    const R = 3959; // Earth's radius in miles
    const dLat = ((point2.lat - point1.lat) * Math.PI) / 180;
    const dLon = ((point2.lng - point1.lng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((point1.lat * Math.PI) / 180) *
        Math.cos((point2.lat * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private getRandomFarmName(): string {
    const names = [
      'Suncoast Family Farm',
      'Green Valley Organics',
      'Harvest Moon Farm',
      'Sunrise Acres',
      'Fresh Fields Farm',
    ];
    return names[Math.floor(Math.random() * names.length)];
  }

  private generateEventTitle(type: FarmEvent['type']): string {
    const titles: Record<FarmEvent['type'], string[]> = {
      market: [
        'Weekly Farmers Market',
        'Saturday Market Special',
        'Farm Fresh Market Day',
      ],
      workshop: [
        'Organic Gardening Workshop',
        'Beekeeping Basics',
        'Composting 101',
        'Seed Starting Class',
      ],
      festival: [
        'Strawberry Festival',
        'Harvest Celebration',
        'Farm to Table Festival',
        'Spring Garden Festival',
      ],
      tour: [
        'Farm Tour & Tasting',
        'Behind the Scenes Tour',
        'Educational Farm Visit',
      ],
      upick: [
        'U-Pick Strawberries',
        'Pick Your Own Tomatoes',
        'Blueberry Picking Day',
      ],
      special: [
        'Farm-to-Table Dinner',
        'Sunset Yoga on the Farm',
        'Kids Farm Day',
      ],
    };
    const options = titles[type];
    return options[Math.floor(Math.random() * options.length)];
  }

  private generateEventDescription(type: FarmEvent['type']): string {
    const descriptions: Record<FarmEvent['type'], string> = {
      market:
        'Join us for fresh, locally-grown produce and handmade goods from local farmers and artisans.',
      workshop:
        'Learn practical skills and sustainable farming techniques from our experienced farmers.',
      festival:
        'Celebrate the season with live music, local food, farm tours, and family activities.',
      tour:
        'Explore our farm, meet the farmers, and learn about sustainable agriculture practices.',
      upick:
        'Pick your own fresh produce right from the field. Bring family and friends for a fun farm experience!',
      special:
        'Special farm event featuring unique experiences and seasonal activities.',
    };
    return descriptions[type];
  }

  private generateEventTags(type: FarmEvent['type']): string[] {
    const baseTags = ['family-friendly', 'outdoor'];
    const typeTags: Record<FarmEvent['type'], string[]> = {
      market: ['fresh-produce', 'local-vendors', 'community'],
      workshop: ['educational', 'hands-on', 'learning'],
      festival: ['seasonal', 'entertainment', 'food'],
      tour: ['educational', 'farm-tour', 'guided'],
      upick: ['hands-on', 'seasonal', 'pick-your-own'],
      special: ['unique-experience', 'limited-time'],
    };
    return [...baseTags, ...typeTags[type]];
  }

  private getSeasonalProducts(): string[] {
    const month = new Date().getMonth();
    const seasonalMap: Record<number, string[]> = {
      0: ['Strawberries', 'Lettuce', 'Herbs'], // January
      1: ['Strawberries', 'Tomatoes', 'Peppers'], // February
      2: ['Tomatoes', 'Cucumbers', 'Squash'], // March
      3: ['Blueberries', 'Tomatoes', 'Peppers'], // April
      4: ['Blueberries', 'Corn', 'Watermelon'], // May
      5: ['Watermelon', 'Corn', 'Tomatoes'], // June
      6: ['Tomatoes', 'Peppers', 'Eggplant'], // July
      7: ['Peppers', 'Okra', 'Sweet Potatoes'], // August
      8: ['Pumpkins', 'Squash', 'Peppers'], // September
      9: ['Pumpkins', 'Sweet Potatoes', 'Lettuce'], // October
      10: ['Lettuce', 'Broccoli', 'Cauliflower'], // November
      11: ['Strawberries', 'Lettuce', 'Broccoli'], // December
    };
    return seasonalMap[month] || ['Seasonal Produce'];
  }
}

// Export singleton instance
export const eventManagementAgent = new EventManagementAgent();
export default eventManagementAgent;
